import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { billingAPI } from '../services/api';
import { useWorkspace } from './WorkspaceContext';
import type { WorkspaceBilling } from '../types/billing';

/**
 * A page-level "refresh everything" source.
 *
 * 🚨 THE BILLING PAGE IS NOT ONE PAYLOAD. `/api/billing/workspace/{id}` is the
 * biggest source but NOT the only one — the invoice ledger, the active-credit
 * ledger and the text top-up history are each their own endpoint, fetched by
 * the component that renders them, on mount. Before this registry the Refresh
 * button only refetched the main payload, so a freshly-paid invoice (or a
 * top-up bought in another tab) stayed invisible until the browser was
 * reloaded. That is exactly the bug the owner reported.
 *
 * Every component on the Billing page that fetches its OWN data must register
 * here via `useBillingRefreshSource`, so `refreshAll()` fans out to all of
 * them and the Refresh button can stay busy until they have all settled.
 */
type RefreshSource = () => Promise<void> | void;

interface BillingContextType {
  billing: WorkspaceBilling | null;
  loading: boolean;
  error: string | null;
  /** Refetch the main billing payload ONLY. Use for a targeted update after a
   *  mutation (plan change, overage toggle) where nothing else can have moved. */
  refetch: () => Promise<void>;
  /** Refetch the main payload AND every registered sibling source. This is
   *  what the Refresh button calls — one click must leave the page in the same
   *  state a full browser reload would. */
  refreshAll: () => Promise<void>;
  /** True while `refreshAll` is in flight (all sources, not just the payload). */
  refreshing: boolean;
  /** Register a sibling data source. Returns the unregister function. Prefer
   *  the `useBillingRefreshSource` hook over calling this directly. */
  registerRefreshSource: (id: string, fn: RefreshSource) => () => void;
  setBilling: React.Dispatch<React.SetStateAction<WorkspaceBilling | null>>;
}

const BillingContext = createContext<BillingContextType | undefined>(undefined);

export const BillingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { activeWorkspace } = useWorkspace();
  const [billing, setBilling] = useState<WorkspaceBilling | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Kept in a ref, not state: registering a source must never re-render the
  // whole provider (every consumer of `billing` would re-render with it), and
  // the set is only ever read at the moment Refresh is clicked.
  const sourcesRef = useRef<Map<string, RefreshSource>>(new Map());

  const registerRefreshSource = useCallback((id: string, fn: RefreshSource) => {
    sourcesRef.current.set(id, fn);
    return () => {
      // Only drop our own entry — a remount that re-registered under the same
      // id before this cleanup ran must keep the newer function.
      if (sourcesRef.current.get(id) === fn) sourcesRef.current.delete(id);
    };
  }, []);

  const fetchBilling = useCallback(async () => {
    if (!activeWorkspace) {
      setBilling(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await billingAPI.getWorkspaceBilling(activeWorkspace.id);
      setBilling(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load billing');
      setBilling(null);
    } finally {
      setLoading(false);
    }
  }, [activeWorkspace]);

  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    try {
      // `allSettled`, never `all`: one endpoint that is dark (the top-up
      // history 404s while the flag is off) or transiently down must not stop
      // the other sources from refreshing, and must not leave the button stuck
      // in its busy state. Each source owns its own error surface.
      await Promise.allSettled([
        fetchBilling(),
        ...Array.from(sourcesRef.current.values()).map(async (fn) => fn()),
      ]);
    } finally {
      setRefreshing(false);
    }
  }, [fetchBilling]);

  useEffect(() => {
    fetchBilling();
  }, [fetchBilling]);

  return (
    <BillingContext.Provider
      value={{
        billing,
        loading,
        error,
        refetch: fetchBilling,
        refreshAll,
        refreshing,
        registerRefreshSource,
        setBilling,
      }}
    >
      {children}
    </BillingContext.Provider>
  );
};

export const useBilling = (): BillingContextType => {
  const ctx = useContext(BillingContext);
  if (!ctx) throw new Error('useBilling must be used within BillingProvider');
  return ctx;
};

/**
 * Register this component's own fetch with the Billing page's Refresh button.
 *
 * `fn` is held in a ref, so an inline closure is fine — it is re-read at click
 * time and the registration itself never churns. `id` must be unique per
 * mounted component; unmounting unregisters automatically.
 */
export const useBillingRefreshSource = (id: string, fn: RefreshSource): void => {
  const { registerRefreshSource } = useBilling();
  const fnRef = useRef<RefreshSource>(fn);

  useEffect(() => {
    fnRef.current = fn;
  });

  useEffect(
    () => registerRefreshSource(id, () => fnRef.current()),
    [id, registerRefreshSource],
  );
};
